import { ApiResult } from "./api_result";
import { fetchWithTimeout } from "./fetch_with_timeout";
import { getConfig } from "services/config.service";
import { isNullOrWhitespace } from "../../services/validation.service";

class HttpService {
  timeoutMs: number;

  constructor(timeoutMs?: number) {
    if (timeoutMs) {
      this.timeoutMs = timeoutMs;
    } else {
      const appConfig = getConfig();
      this.timeoutMs = Number(appConfig.httpTimeoutMs);
    }
  }

  private buildPath(path: string): string {
    const backendUrl = getConfig().backendUrl!;
    let url = path;
    if (isNullOrWhitespace(backendUrl)) {
      url = backendUrl + path;
    }
    return url;
  }

  private async safeParseJson<T>(response: Response): Promise<T | undefined> {
    try {
      return await response.json();
    } catch {
      return undefined;
    }
  }

  protected async get<T>(path: string): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<T>().withStatusCode(response.status);
      const data = await this.safeParseJson<T>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<T>().withTimeout();
    }
  }

  protected async getAnonym<ReturnType>(
    path: string,
  ): Promise<ApiResult<ReturnType>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<ReturnType>().withStatusCode(
        response.status,
      );
      const data = await this.safeParseJson<ReturnType>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch (error) {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<ReturnType>().withTimeout();
    }
  }

  protected async downloadFile(path: string): Promise<ApiResult<Blob>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "GET",
        timeout: this.timeoutMs,
      });

      if (response.ok) {
        const fileData = await response.blob();
        return new ApiResult<Blob>()
          .withStatusCode(response.status)
          .withData(fileData);
      } else {
        return new ApiResult<Blob>().withStatusCode(response.status);
      }
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<Blob>().withTimeout();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async post<T>(path: string, content: T): Promise<ApiResult<any>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        body: JSON.stringify(content),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = new ApiResult<any>().withStatusCode(response.status);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await this.safeParseJson<any>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return new ApiResult<any>().withTimeout();
    }
  }

  protected async postForId<T>(
    path: string,
    content: T,
  ): Promise<ApiResult<number>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        body: JSON.stringify(content),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<number>().withStatusCode(response.status);
      if (response.ok) {
        const data = await response.text();
        result.withData(parseInt(data));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = await this.safeParseJson<any>(response);
        if (data !== undefined) {
          result.withData(data);
        }
      }
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<number>().withTimeout();
    }
  }

  protected async postForData<T>(path: string): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<T>().withStatusCode(response.status);
      const data = await this.safeParseJson<T>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<T>().withTimeout();
    }
  }

  protected async postDataForData<TIn, TOut>(
    path: string,
    content: TIn,
    timeoutMs: number | null = this.timeoutMs,
  ): Promise<ApiResult<TOut>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify(content),
        timeout: timeoutMs === null ? undefined : timeoutMs,
      });
      const result = new ApiResult<TOut>().withStatusCode(response.status);
      const data = await this.safeParseJson<TOut>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch {
      if (timeoutMs === null) {
        console.warn("HTTP request failed");
        return new ApiResult<TOut>().withStatusCode(500);
      }

      console.warn(`HTTP timeout after ${timeoutMs} ms`);
      return new ApiResult<TOut>().withTimeout();
    }
  }

  protected async postForm(
    path: string,
    content: FormData,
  ): Promise<ApiResult<void>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        body: content,
        timeout: this.timeoutMs,
      });
      return new ApiResult<void>().withStatusCode(response.status);
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<void>().withTimeout();
    }
  }

  protected async uploadFileForm<T>(
    path: string,
    content: FormData,
    onUploadProgress?: (percent: number) => void,
  ): Promise<ApiResult<T>> {
    return await new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", this.buildPath(path), true);
      xhr.timeout = this.timeoutMs;

      xhr.upload.onprogress = (event) => {
        if (!onUploadProgress || !event.lengthComputable || event.total <= 0) {
          return;
        }

        onUploadProgress((event.loaded / event.total) * 100);
      };

      xhr.onload = () => {
        const result = new ApiResult<T>().withStatusCode(xhr.status);

        if (xhr.responseText) {
          try {
            result.withData(JSON.parse(xhr.responseText) as T);
          } catch {
            // Non-JSON response body is acceptable for this API wrapper.
          }
        }

        resolve(result);
      };

      xhr.onerror = () => {
        resolve(new ApiResult<T>().withStatusCode(500));
      };

      xhr.ontimeout = () => {
        console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
        resolve(new ApiResult<T>().withTimeout());
      };

      xhr.send(content);
    });
  }

  protected async editFileForm<T>(
    path: string,
    content: FormData,
  ): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "PUT",
        body: content,
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<T>().withStatusCode(response.status);
      const data = await this.safeParseJson<T>(response);
      if (data !== undefined) result.withData(data);
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<T>().withTimeout();
    }
  }

  protected async put<T>(path: string, content?: T): Promise<ApiResult<void>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "PUT",
        body: content ? JSON.stringify(content) : null,
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });

      const apiResult = new ApiResult<void>().withStatusCode(response.status);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await this.safeParseJson<any>(response);
      if (data !== undefined) {
        apiResult.withData(data);
      }

      return apiResult;
    } catch (error) {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`, error);
      return new ApiResult<void>().withTimeout();
    }
  }

  protected async putForData<TOut>(
    path: string,
    content?: unknown,
  ): Promise<ApiResult<TOut>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "PUT",
        body: content ? JSON.stringify(content) : null,
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });

      const apiResult = new ApiResult<TOut>().withStatusCode(response.status);
      const data = await this.safeParseJson<TOut>(response);
      if (data !== undefined) {
        apiResult.withData(data);
      }

      return apiResult;
    } catch (error) {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`, error);
      return new ApiResult<TOut>().withTimeout();
    }
  }

  protected async delete<T>(
    path: string,
    content?: T,
  ): Promise<ApiResult<void>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "DELETE",
        body: content ? JSON.stringify(content) : null,
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<void>().withStatusCode(response.status);
      const data = await this.safeParseJson<any>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch (error) {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`, error);
      return new ApiResult<void>().withTimeout();
    }
  }

  /**
   * Fire-and-forget DELETE that survives the page being closed.
   *
   * `navigator.sendBeacon` is the usual tool here but can only issue POST, and adding a POST
   * alias purely to work around that would put transport limitations into the API surface.
   * `keepalive` lets the browser finish the request after unload while keeping the verb honest.
   */
  protected deleteOnUnload(path: string): void {
    try {
      void fetch(this.buildPath(path), {
        method: "DELETE",
        keepalive: true,
      });
    } catch {
      // The page is going away; a failed cleanup call is picked up by the server-side
      // stale-session sweep instead.
    }
  }
}

export default HttpService;
